import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Edit, Trash2 } from 'lucide-react';
import pointsService from '../services/pointsService';
import './PointsRulesManagement.css';

const PointsRulesManagement = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') === 'redemption' ? 'redemption' : 'earning');
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [ruleToDelete, setRuleToDelete] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        rule_name: '',
        calculation_type: 'percentage',
        points_value: '',
        money_value: '',
        percentage_value: '',
        min_order_value: '',
        max_points_per_order: '',
        is_active: true
    });

    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            next.set('tab', activeTab);
            return next;
        }, { replace: true });
        fetchRules();
    }, [activeTab, setSearchParams]);

    useEffect(() => {
        const action = searchParams.get('action');
        if (!action || showModal) return;
        if (action === 'add') {
            handleOpenModal();
        } else if (action === 'edit') {
            const id = Number(searchParams.get('id'));
            const rule = rules.find((item) => item.id === id);
            if (rule) handleOpenModal(rule);
        }
    }, [rules]);

    // Prevent mouse wheel from changing number input values
    useEffect(() => {
        const handleWheel = (e) => {
            if (document.activeElement.type === 'number') {
                document.activeElement.blur();
            }
        };

        document.addEventListener('wheel', handleWheel, { passive: true });

        return () => {
            document.removeEventListener('wheel', handleWheel);
        };
    }, []);

    const fetchRules = async () => {
        try {
            setLoading(true);
            const response = await pointsService.getPointsRules(activeTab);
            if (response.success) {
                setRules(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching rules:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (rule = null) => {
        if (rule) {
            setEditingRule(rule);
            setFormData({
                rule_name: rule.rule_name,
                calculation_type: rule.calculation_type,
                points_value: rule.points_value,
                money_value: rule.money_value,
                percentage_value: rule.percentage_value || '',
                min_order_value: rule.min_order_value || '',
                max_points_per_order: rule.max_points_per_order || '',
                is_active: rule.is_active
            });
        } else {
            setEditingRule(null);
            setFormData({
                rule_name: '',
                calculation_type: 'percentage',
                points_value: '',
                money_value: '',
                percentage_value: '',
                min_order_value: '',
                max_points_per_order: '',
                is_active: true
            });
        }
        setFormErrors({});
        setShowModal(true);
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            next.set('tab', activeTab);
            next.set('action', rule ? 'edit' : 'add');
            if (rule) next.set('id', String(rule.id)); else next.delete('id');
            return next;
        }, { replace: true });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingRule(null);
        setFormData({
            rule_name: '',
            calculation_type: 'percentage',
            points_value: '',
            money_value: '',
            percentage_value: '',
            min_order_value: '',
            max_points_per_order: '',
            is_active: true
        });
        setFormErrors({});
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            next.delete('action');
            next.delete('id');
            return next;
        }, { replace: true });
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.rule_name.trim()) {
            errors.rule_name = 'Rule name is required';
        }

        if (activeTab === 'earning') {
            if (formData.calculation_type === 'percentage') {
                if (!formData.percentage_value || parseFloat(formData.percentage_value) <= 0) {
                    errors.percentage_value = 'Percentage must be greater than 0';
                }
            } else {
                if (!formData.points_value || parseInt(formData.points_value) <= 0) {
                    errors.points_value = 'Points value must be greater than 0';
                }
                if (!formData.money_value || parseFloat(formData.money_value) <= 0) {
                    errors.money_value = 'Money value must be greater than 0';
                }
            }
        } else {
            // Redemption rules
            if (!formData.points_value || parseInt(formData.points_value) <= 0) {
                errors.points_value = 'Points value must be greater than 0';
            }
            if (!formData.money_value || parseFloat(formData.money_value) <= 0) {
                errors.money_value = 'Money value must be greater than 0';
            }
        }

        if (formData.min_order_value && parseFloat(formData.min_order_value) < 0) {
            errors.min_order_value = 'Minimum order value cannot be negative';
        }

        if (formData.max_points_per_order && parseInt(formData.max_points_per_order) <= 0) {
            errors.max_points_per_order = 'Maximum points must be greater than 0';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const ruleData = {
                rule_type: activeTab,
                rule_name: formData.rule_name,
                calculation_type: formData.calculation_type,
                points_value: parseInt(formData.points_value) || 0,
                money_value: parseFloat(formData.money_value) || 0,
                percentage_value: formData.percentage_value ? parseFloat(formData.percentage_value) : null,
                min_order_value: formData.min_order_value ? parseFloat(formData.min_order_value) : null,
                max_points_per_order: formData.max_points_per_order ? parseInt(formData.max_points_per_order) : null,
                is_active: formData.is_active
            };

            let response;
            if (editingRule) {
                response = await pointsService.updatePointsRule(editingRule.id, ruleData);
            } else {
                response = await pointsService.createPointsRule(ruleData);
            }

            if (response.success) {
                handleCloseModal();
                fetchRules();
            }
        } catch (error) {
            console.error('Error saving rule:', error);
            setFormErrors({ submit: error.message || 'Failed to save rule' });
        }
    };

    const handleToggleStatus = async (rule) => {
        try {
            const response = await pointsService.toggleRuleStatus(rule.id);

            if (response.success) {
                fetchRules();
            }
        } catch (error) {
            console.error('Error toggling status:', error);
        }
    };

    const handleDeleteClick = (rule) => {
        setRuleToDelete(rule);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!ruleToDelete) return;

        try {
            const response = await pointsService.deletePointsRule(ruleToDelete.id, activeTab);
            if (response.success) {
                setShowDeleteConfirm(false);
                setRuleToDelete(null);
                fetchRules();
            }
        } catch (error) {
            console.error('Error deleting rule:', error);
        }
    };

    const calculatePreview = () => {
        const orderValue = 500;

        if (activeTab === 'earning') {
            if (formData.calculation_type === 'percentage' && formData.percentage_value) {
                const percentage = parseFloat(formData.percentage_value);
                const points = Math.floor((orderValue * percentage) / 100);
                return `Order of ₹${orderValue} will earn ${points} points`;
            } else if (formData.calculation_type === 'fixed' && formData.money_value && formData.points_value) {
                const moneyValue = parseFloat(formData.money_value);
                const pointsValue = parseInt(formData.points_value);
                const points = Math.floor((orderValue / moneyValue) * pointsValue);
                return `Order of ₹${orderValue} will earn ${points} points`;
            }
        } else {
            // Redemption
            if (formData.points_value && formData.money_value) {
                const pointsValue = parseInt(formData.points_value);
                const moneyValue = parseFloat(formData.money_value);
                const testPoints = 500;
                const discount = (testPoints / pointsValue) * moneyValue;
                return `${testPoints} points can be redeemed for ₹${discount.toFixed(2)} discount`;
            }
        }

        return 'Enter values to see preview';
    };

    return (
        <div className="points-rules-container">
            {/* Header */}
            <div className="points-rules-header">
                <h1>Points & Rewards Configuration</h1>
                <p>Manage earning and redemption rules for your reward points system</p>
            </div>

            {/* Tabs */}
            <div className="points-rules-tabs">
                <button
                    className={`tab-button ${activeTab === 'earning' ? 'active' : ''}`}
                    onClick={() => setActiveTab('earning')}
                >
                    Earning Rules
                </button>
                <button
                    className={`tab-button ${activeTab === 'redemption' ? 'active' : ''}`}
                    onClick={() => setActiveTab('redemption')}
                >
                    Redemption Rules
                </button>
            </div>

            {/* Content */}
            <div className="points-rules-content">
                <div className="content-header">
                    <h2>{activeTab === 'earning' ? 'Earning Rules' : 'Redemption Rules'}</h2>
                    <button className="btn-add" onClick={() => handleOpenModal()}>
                        <Plus size={18} />
                        Add {activeTab === 'earning' ? 'Earning' : 'Redemption'} Rule
                    </button>
                </div>

                {/* Rules Table */}
                {loading ? (
                    <div className="loading-state">Loading rules...</div>
                ) : rules.length === 0 ? (
                    <div className="empty-state">
                        <p>No {activeTab} rules configured yet</p>
                        <button className="btn-primary" onClick={() => handleOpenModal()}>
                            Create First Rule
                        </button>
                    </div>
                ) : (
                    <div className="rules-table-card">
                        <table className="rules-table">
                            <thead>
                                <tr>
                                    <th>Rule Name</th>
                                    <th>Calculation</th>
                                    <th>Points/Money Ratio</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rules.map((rule) => (
                                    <tr key={rule.id}>
                                        <td>
                                            <div className="rule-name">{rule.rule_name}</div>
                                        </td>
                                        <td>
                                            <span className="calculation-badge">
                                                {rule.calculation_type === 'percentage' ? 'Percentage' : 'Fixed'}
                                            </span>
                                        </td>
                                        <td>
                                            {activeTab === 'earning' && rule.calculation_type === 'percentage' ? (
                                                <span>{rule.percentage_value}% of order value</span>
                                            ) : (
                                                <span>{rule.points_value} points = ₹{rule.money_value}</span>
                                            )}
                                        </td>
                                        <td>
                                            <label className="points-toggle" title={rule.is_active ? 'Deactivate rule' : 'Activate rule'}>
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(rule.is_active)}
                                                    onChange={() => handleToggleStatus(rule)}
                                                />
                                                <span className="points-toggle-slider" />
                                                <em>{rule.is_active ? 'Active' : 'Inactive'}</em>
                                            </label>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    className="cm-edit-btn"
                                                    onClick={() => handleOpenModal(rule)}
                                                    title="Edit"
                                                >
                                                    <Edit size={18} />
                                                </button>
                                                <button
                                                    className="cm-delete-btn"
                                                    onClick={() => handleDeleteClick(rule)}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal-overlay-new" onClick={handleCloseModal}>
                    <div className="modal-content-new" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-new">
                            <h2 className="modal-title-new">{editingRule ? 'Edit' : 'Add'} {activeTab === 'earning' ? 'Earning' : 'Redemption'} Rule</h2>
                            <button className="modal-close-new" onClick={handleCloseModal}>×</button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-new">
                                {/* Rule Name */}
                                <div className="form-group">
                                    <label htmlFor="rule_name">Rule Name *</label>
                                    <input
                                        type="text"
                                        id="rule_name"
                                        value={formData.rule_name}
                                        onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
                                        placeholder="e.g., Earn 10% points on purchase"
                                        className={formErrors.rule_name ? 'error' : ''}
                                    />
                                    {formErrors.rule_name && <span className="error-text">{formErrors.rule_name}</span>}
                                </div>

                                {/* Calculation Type (Earning only) */}
                                {activeTab === 'earning' && (
                                    <div className="form-group">
                                        <label>Calculation Type *</label>
                                        <div className="radio-group">
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="calculation_type"
                                                    value="percentage"
                                                    checked={formData.calculation_type === 'percentage'}
                                                    onChange={(e) => setFormData({ ...formData, calculation_type: e.target.value })}
                                                />
                                                <span>Percentage</span>
                                            </label>
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="calculation_type"
                                                    value="fixed"
                                                    checked={formData.calculation_type === 'fixed'}
                                                    onChange={(e) => setFormData({ ...formData, calculation_type: e.target.value })}
                                                />
                                                <span>Fixed</span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {/* Percentage Value (Earning - Percentage) */}
                                {activeTab === 'earning' && formData.calculation_type === 'percentage' && (
                                    <div className="form-group">
                                        <label htmlFor="percentage_value">Points Percentage *</label>
                                        <input
                                            type="number"
                                            id="percentage_value"
                                            value={formData.percentage_value}
                                            onChange={(e) => setFormData({ ...formData, percentage_value: e.target.value })}
                                            placeholder="e.g., 10 (means 10% of order value)"
                                            step="1"
                                            min="0"
                                            max="100"
                                            className={formErrors.percentage_value ? 'error' : ''}
                                        />
                                        {formErrors.percentage_value && <span className="error-text">{formErrors.percentage_value}</span>}
                                        <span className="help-text">Percentage of order value to convert to points</span>
                                    </div>
                                )}

                                {/* Fixed Values (Earning - Fixed or Redemption) */}
                                {(activeTab === 'redemption' || (activeTab === 'earning' && formData.calculation_type === 'fixed')) && (
                                    <>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="money_value">Money Value (₹) *</label>
                                                <input
                                                    type="number"
                                                    id="money_value"
                                                    value={formData.money_value}
                                                    onChange={(e) => setFormData({ ...formData, money_value: e.target.value })}
                                                    placeholder="e.g., 10"
                                                    step="1"
                                                    min="0"
                                                    className={formErrors.money_value ? 'error' : ''}
                                                />
                                                {formErrors.money_value && <span className="error-text">{formErrors.money_value}</span>}
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="points_value">Points Value *</label>
                                                <input
                                                    type="number"
                                                    id="points_value"
                                                    value={formData.points_value}
                                                    onChange={(e) => setFormData({ ...formData, points_value: e.target.value })}
                                                    placeholder="e.g., 100"
                                                    min="0"
                                                    className={formErrors.points_value ? 'error' : ''}
                                                />
                                                {formErrors.points_value && <span className="error-text">{formErrors.points_value}</span>}
                                            </div>
                                        </div>

                                        <div className="calculation-display">
                                            {activeTab === 'earning' ? (
                                                <span>Spend ₹{formData.money_value || '0'} → Earn {formData.points_value || '0'} points</span>
                                            ) : (
                                                <span>{formData.points_value || '0'} points = ₹{formData.money_value || '0'} discount</span>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Optional Fields - COMMENTED OUT (Not currently used) */}
                                {/* {activeTab === 'earning' && (
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="min_order_value">Minimum Order Value (₹)</label>
                                            <input
                                                type="number"
                                                id="min_order_value"
                                                value={formData.min_order_value}
                                                onChange={(e) => setFormData({ ...formData, min_order_value: e.target.value })}
                                                placeholder="Optional"
                                                step="1"
                                                min="0"
                                                className={formErrors.min_order_value ? 'error' : ''}
                                            />
                                            {formErrors.min_order_value && <span className="error-text">{formErrors.min_order_value}</span>}
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="max_points_per_order">Maximum Points Per Order</label>
                                            <input
                                                type="number"
                                                id="max_points_per_order"
                                                value={formData.max_points_per_order}
                                                onChange={(e) => setFormData({ ...formData, max_points_per_order: e.target.value })}
                                                placeholder="Optional"
                                                min="0"
                                                className={formErrors.max_points_per_order ? 'error' : ''}
                                            />
                                            {formErrors.max_points_per_order && <span className="error-text">{formErrors.max_points_per_order}</span>}
                                        </div>
                                    </div>
                                )} */}

                                {/* Status Toggle */}
                                <div className="form-group">
                                    <label htmlFor="status-toggle">Status</label>
                                    <div className="toggle-switch">
                                        <input
                                            id="status-toggle"
                                            type="checkbox"
                                            checked={formData.is_active}
                                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        />
                                        <span className="toggle-slider"></span>
                                    </div>
                                </div>

                                {/* Preview */}
                                <div className="preview-box">
                                    <h4>Preview</h4>
                                    <p>{calculatePreview()}</p>
                                </div>

                                {formErrors.submit && (
                                    <div className="error-message">{formErrors.submit}</div>
                                )}
                            </div>

                            <div className="modal-footer-new">
                                <button type="button" className="btn-secondary-new" onClick={handleCloseModal}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary-new">
                                    {editingRule ? 'Update' : 'Create'} Rule
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal-overlay-new" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="modal-content-new modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-new">
                            <h2 className="modal-title-new">Confirm Delete</h2>
                            <button className="modal-close-new" onClick={() => setShowDeleteConfirm(false)}>×</button>
                        </div>
                        <div className="modal-body-new">
                            <p>Are you sure you want to delete the rule "{ruleToDelete?.rule_name}"?</p>
                            <p className="warning-text">This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer-new">
                            <button className="btn-secondary-new" onClick={() => setShowDeleteConfirm(false)}>
                                Cancel
                            </button>
                            <button className="btn-reject-new" onClick={handleDeleteConfirm}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PointsRulesManagement;
